import React, { useRef, useState } from 'react';
import { Bold, Italic, List, ListOrdered, RotateCcw, PlusCircle } from 'lucide-react';

const RichTextarea = ({
  value = '',
  onChange,
  placeholder = '',
  minHeight = '180px',
  maxHeight = '420px',
  style = {}
}) => {
  const textareaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Helper to update value and maintain cursor position
  const updateText = (newText, newCursorStart, newCursorEnd) => {
    if (onChange) {
      onChange({ target: { value: newText } });
    }
    setTimeout(() => {
      if (textareaRef.current && newCursorStart !== undefined) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(
          newCursorStart,
          newCursorEnd !== undefined ? newCursorEnd : newCursorStart
        );
      }
    }, 10);
  };

  // Wrap selection with format marks (e.g. bold or italic)
  const handleWrapFormat = (prefix, suffix = prefix) => {
    const el = textareaRef.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = el.value || '';
    const selected = text.substring(start, end);

    if (!selected) {
      // If nothing selected, insert placeholder
      const insert = `${prefix}text${suffix}`;
      const newText = text.substring(0, start) + insert + text.substring(end);
      updateText(newText, start + prefix.length, start + prefix.length + 4);
      return;
    }

    const newText = text.substring(0, start) + prefix + selected + suffix + text.substring(end);
    updateText(newText, start + prefix.length, end + prefix.length);
  };

  // Apply Bullet or Numbered List to selected lines or all lines
  const handleListFormat = (type) => {
    const el = textareaRef.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = el.value || '';

    // If nothing selected, format entire text into list lines
    let rangeStart = text.lastIndexOf('\n', start - 1);
    rangeStart = rangeStart === -1 ? 0 : rangeStart + 1;

    let rangeEnd = text.indexOf('\n', end);
    rangeEnd = rangeEnd === -1 ? text.length : rangeEnd;

    const before = text.substring(0, rangeStart);
    const middle = text.substring(rangeStart, rangeEnd);
    const after = text.substring(rangeEnd);

    const lines = middle.split('\n');
    let counter = 1;

    const newLines = lines.map(line => {
      // Strip existing numbering or bullet
      const cleanLine = line.replace(/^(\d+\.|\•|\-|\*)\s*/, '');
      if (type === 'numbered') {
        return `${counter++}. ${cleanLine}`;
      } else {
        return `• ${cleanLine}`;
      }
    });

    const replaced = newLines.join('\n');
    const newText = before + replaced + after;
    updateText(newText, rangeStart, rangeStart + replaced.length);
  };

  // Auto-indent & auto-number on Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const el = textareaRef.current;
      if (!el) return;

      const cursor = el.selectionStart;
      const text = el.value || '';

      // Find current line up to cursor
      const lastNewLine = text.lastIndexOf('\n', cursor - 1);
      const currentLine = text.substring(lastNewLine === -1 ? 0 : lastNewLine + 1, cursor);

      // Check if numbered list (e.g. "1. " or "12. ")
      const numMatch = currentLine.match(/^(\d+)\.\s*(.*)$/);
      if (numMatch) {
        e.preventDefault();
        const num = parseInt(numMatch[1], 10);
        const content = numMatch[2].trim();

        if (content === '') {
          // Empty line: user wants to exit numbered list
          const beforeLine = text.substring(0, lastNewLine === -1 ? 0 : lastNewLine);
          const afterCursor = text.substring(cursor);
          const newText = (beforeLine ? beforeLine + '\n' : '') + afterCursor;
          updateText(newText, lastNewLine === -1 ? 0 : lastNewLine + 1);
        } else {
          // Increment number
          const insert = `\n${num + 1}. `;
          const newText = text.substring(0, cursor) + insert + text.substring(cursor);
          updateText(newText, cursor + insert.length);
        }
        return;
      }

      // Check if bullet list (e.g. "• ")
      const bulletMatch = currentLine.match(/^[•\-\*]\s*(.*)$/);
      if (bulletMatch) {
        e.preventDefault();
        const content = bulletMatch[1].trim();

        if (content === '') {
          // Empty bullet: exit list
          const beforeLine = text.substring(0, lastNewLine === -1 ? 0 : lastNewLine);
          const afterCursor = text.substring(cursor);
          const newText = (beforeLine ? beforeLine + '\n' : '') + afterCursor;
          updateText(newText, lastNewLine === -1 ? 0 : lastNewLine + 1);
        } else {
          const insert = `\n• `;
          const newText = text.substring(0, cursor) + insert + text.substring(cursor);
          updateText(newText, cursor + insert.length);
        }
        return;
      }
    }
  };

  const handleAddNewItem = () => {
    const text = value || '';
    const lines = text.split('\n').filter(Boolean);
    const lastNumMatch = lines.length > 0 ? lines[lines.length - 1].match(/^(\d+)\./) : null;
    const nextNum = lastNumMatch ? parseInt(lastNumMatch[1], 10) + 1 : (lines.length + 1);
    
    const append = text.endsWith('\n') || !text ? `${nextNum}. ` : `\n${nextNum}. `;
    const newText = text + append;
    updateText(newText, newText.length);
  };

  const lineCount = (value || '').split('\n').filter(Boolean).length;
  const charCount = (value || '').length;

  return (
    <div
      style={{
        border: isFocused ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        ...style
      }}
    >
      {/* Rich Text Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          gap: '6px',
          flexWrap: 'wrap'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <button
            type="button"
            onClick={() => handleWrapFormat('**')}
            title="Bold (**text**)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '5px 7px',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              color: '#334155',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 700
            }}
          >
            <Bold size={14} />
          </button>

          <button
            type="button"
            onClick={() => handleWrapFormat('*')}
            title="Italic (*text*)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '5px 7px',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              color: '#334155',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            <Italic size={14} />
          </button>

          <div style={{ width: '1px', height: '18px', backgroundColor: '#cbd5e1', margin: '0 4px' }} />

          <button
            type="button"
            onClick={() => handleListFormat('numbered')}
            title="Numbered List (1. 2. 3.)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '5px 9px',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              color: '#334155',
              cursor: 'pointer',
              fontSize: '11.5px',
              fontWeight: 600
            }}
          >
            <ListOrdered size={14} /> 1. 2. 3.
          </button>

          <button
            type="button"
            onClick={() => handleListFormat('bullet')}
            title="Bullet List (• • •)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '5px 9px',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              color: '#334155',
              cursor: 'pointer',
              fontSize: '11.5px',
              fontWeight: 600
            }}
          >
            <List size={14} /> Bullet
          </button>

          <button
            type="button"
            onClick={handleAddNewItem}
            title="Add Next Numbered Item"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '5px 9px',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              color: '#2563eb',
              cursor: 'pointer',
              fontSize: '11.5px',
              fontWeight: 600
            }}
          >
            <PlusCircle size={14} /> Add Item
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
            {lineCount} {lineCount === 1 ? 'item' : 'items'}
          </span>
          <button
            type="button"
            onClick={() => updateText('', 0)}
            title="Clear all text"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '4px 8px',
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            <RotateCcw size={12} /> Clear
          </button>
        </div>
      </div>

      {/* Editing Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        style={{
          width: '100%',
          minHeight,
          maxHeight,
          padding: '12px 14px',
          border: 'none',
          outline: 'none',
          fontSize: '0.85rem',
          lineHeight: '1.65',
          color: '#1e293b',
          fontFamily: 'inherit',
          resize: 'vertical',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box'
        }}
      />

      {/* Bottom Info Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 10px',
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f1f5f9',
          fontSize: '10.5px',
          color: '#94a3b8'
        }}
      >
        <span>Press <kbd style={{ padding: '1px 4px', background: '#e2e8f0', borderRadius: '3px' }}>Enter</kbd> to automatically number next item</span>
        <span>{charCount} characters</span>
      </div>
    </div>
  );
};

export default RichTextarea;
